import z from 'zod'
import { ErrorBox } from '@/components/commons/error-box'

import { AlertTriangleIcon } from '@/components/ui/icons'
import { ListingDetailsHeaderBase } from '@/features/luxe-submissions/components/listing-details-header-base'
import {
	type ListingDetailsField,
	ListingDetailsInfoBase
} from '@/features/luxe-submissions/components/listing-details-info-base'
import { ListingDetailsLoading } from '@/features/luxe-submissions/components/listing-details-loading'
import { ListingDetailsPhoto } from '@/features/luxe-submissions/components/listing-details-photo'
import { useGetMMSubmissionDetails } from '@/features/luxe-submissions/queries/use-get-mm-submission-details'
import { raise } from '@/helpers/utils'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

function LuxeSubmissionsMMOverviewSubmissionDetails() {
	const { t } = useTranslation()
	const urlParams = useURLParsedParams(
		z.object({
			id: z.string()
		})
	)

	const listingId = urlParams.success
		? String(urlParams.data.id)
		: raise('Invalid listing ID in URL parameters')

	const { data, isLoading, isError } = useGetMMSubmissionDetails({
		listingId
	})

	const listingDetails = data?.data

	if (isLoading) {
		return <ListingDetailsLoading />
	}
	if (!data || isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}

	const fields: ListingDetailsField[] = [
		{
			label: t('luxe_submissions:host_email'),
			value: listingDetails?.hostEmail
		},
		{
			label: t('luxe_submissions:location_column'),
			value: listingDetails?.location ?? '-'
		},
		{
			label: t('luxe_submissions:listing_url'),
			value: listingDetails?.listingUrl ? (
				<a
					className='break-all hover:underline'
					href={listingDetails.listingUrl}
					rel='noopener noreferrer'
					target='_blank'
				>
					{listingDetails.listingUrl}
				</a>
			) : (
				'-'
			)
		},
		{
			label: t('luxe_submissions:listing_name'),
			value: listingDetails?.listingName ?? '-'
		},
		{
			label: t('luxe_submissions:geo'),
			value: listingDetails?.propertyAddress ?? '-'
		},
		{
			label: t('luxe_submissions:description'),
			value: listingDetails?.description ?? '-'
		},
		{
			label: t('luxe_submissions:bedrooms_count'),
			value: listingDetails?.bedrooms ?? '-'
		},
		{
			label: t('luxe_submissions:bed_count'),
			value: listingDetails?.beds ?? '-'
		},
		{
			label: t('luxe_submissions:bathrooms_count'),
			value: listingDetails?.bathrooms ?? '-'
		},
		{
			label: t('luxe_submissions:guest_count'),
			value: listingDetails?.maxGuests ?? '-'
		},
		{
			label: t('luxe_submissions:photos_count'),
			value: listingDetails?.images.length ?? '-'
		},
		{
			label: t('luxe_submissions:photo_url'),
			value: listingDetails?.photoRepositoryUrl ? (
				<a
					className='break-all hover:underline'
					href={listingDetails.photoRepositoryUrl}
					rel='noopener noreferrer'
					target='_blank'
				>
					{listingDetails.photoRepositoryUrl}
				</a>
			) : (
				'-'
			)
		}
	]

	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			<ListingDetailsHeaderBase
				backUrl={routes.LUXE_SUBMISSIONS_MM_SUBMISSIONS.handle.fullPath}
				inventory={listingDetails?.listingInventory}
				listingId={
					listingDetails?.airbnbListingId ?? listingDetails?.id
				}
				listingLink={listingDetails?.listingUrl}
				location={listingDetails?.location}
				title={t('luxe_submissions:listing_submission_for')}
			/>

			<div className='grid h-full grid-cols-[2fr_1fr] overflow-hidden pl-4'>
				<ListingDetailsPhoto
					photos={listingDetails?.images ?? []}
					repositoryUrl={listingDetails?.photoRepositoryUrl}
				/>
				<ListingDetailsInfoBase fields={fields} />
			</div>
		</div>
	)
}

export { LuxeSubmissionsMMOverviewSubmissionDetails }
